import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, first, Observable, of, switchMap, tap } from 'rxjs';
import AppValues from 'src/app/common/app.values';
import { NotificationContextAttributesService } from 'src/app/notifications/notifications-context-attributes.service';
import { NotificationService } from 'src/app/notifications/notifications.service';
import { DeactivationStatus, EmptyResponse, ReactivationPetitionStatus } from 'src/app/swagger-api';
import { DefaultService } from 'src/app/swagger-api/api/default.service';

@Injectable({
  providedIn: 'root',
})
export class ManageProfileService {
  private apiService = inject(DefaultService);
  private notificationService = inject(NotificationService);
  private notificationContextService = inject(NotificationContextAttributesService);

  public confirmDeactivateAccount(accountId: string): Observable<boolean> {
    return this.notificationService
      .deactivateAccountDialog(this.notificationContextService.getDeactivateDialogueArgs('Deactivate Account'))
      .pipe(
        switchMap((reason: string): Observable<boolean> => {
          // If modal closes with a ban reason: the deactivation is confirmed
          if (reason && reason.length > 0)
            return this.deactivateAccount(accountId, reason).pipe(switchMap(() => of(true)));

          // If it is canceled or closed, then it is not confirmed
          return of(false);
        }),
      )
      .pipe(first());
  }

  public confirmSendReactivationPetition(email: string): Observable<boolean> {
    return this.notificationService
      .sendReactivationPetition(
        this.notificationContextService.getSendReactivateRequestDialogArgs('Account Reactivation Request', email),
      )
      .pipe(
        switchMap((request: { email?: string; description?: string } | undefined): Observable<boolean> => {
          if (request && request.email && request.description) {
            return this.updateReactivationPetition(
              request.email,
              request.description,
              ReactivationPetitionStatus.OPEN,
            ).pipe(
              catchError(() => of(false)),
              switchMap(() => of(true)),
            );
          }
          return of(false);
        }),
      )
      .pipe(first());
  }

  public revertAccountReactivationPetition(email: string): Observable<EmptyResponse> {
    return this.updateReactivationPetition(email, AppValues.emptyCharacter, ReactivationPetitionStatus.REVERTED);
  }

  public confirmDeclineAccountReactivationPetition(email: string): Observable<boolean> {
    return this.notificationService
      .declineAccountReactivationPetition(
        this.notificationContextService.getDeclineReactivationArgs('Decline reactivation request', email),
      )
      .pipe(
        switchMap((description: string): Observable<boolean> => {
          if (description && description.length > 0) {
            return this.updateReactivationPetition(email, description, ReactivationPetitionStatus.REJECTED).pipe(
              switchMap(() => of(true)),
            );
          }
          return of(false);
        }),
      )
      .pipe(first());
  }

  public updateReactivationPetition(
    email: string,
    information: string,
    reactivationPetitionStatus: ReactivationPetitionStatus,
  ): Observable<EmptyResponse> {
    return this.apiService
      .restAuthDeactivatePutReactivationPetition({
        email,
        information,
        reactivation_petition_status: reactivationPetitionStatus,
      })
      .pipe(
        first(),
        tap(() => {
          if (reactivationPetitionStatus === ReactivationPetitionStatus.OPEN)
            this.showSuccessfulReactivationPetitionOpenedDialog();

          if (reactivationPetitionStatus === ReactivationPetitionStatus.REJECTED)
            this.showSuccessfulReactivationPetitionDeclineDialog();
        }),
      );
  }

  public reactivateAccount(accountId: string): Observable<EmptyResponse> {
    return this.apiService
      .restAuthDeactivatePut({ deactivation_status: DeactivationStatus.REVERTED }, accountId)
      .pipe(catchError(() => EMPTY));
  }

  private deactivateAccount(accountId: string, reason: string): Observable<EmptyResponse> {
    return this.apiService.restAuthDeactivatePost({ reason }, accountId).pipe(
      catchError(() => EMPTY),
      tap(() => {
        this.showSuccessfulDeactivationDialog();
      }),
    );
  }

  private showSuccessfulDeactivationDialog(): void {
    this.notificationService.showSuccessfulMessage('DIALOG_DEACTIVATION_DESCRIPTION');
  }

  private showSuccessfulReactivationPetitionOpenedDialog(): void {
    this.notificationService.showSuccessfulMessage('DIALOG_REACTIVATION_PETITION_OPEN_DESCRIPTION');
  }

  private showSuccessfulReactivationPetitionDeclineDialog(): void {
    this.notificationService.showSuccessfulMessage('DIALOG_REACTIVATION_PETITION_DECLINE_DESCRIPTION');
  }
}
